import firebase from "firebase/compat/app";
import { Validator } from "@pineapplelab/validators";
import { camelToSnakeCase, isArray, isNotEmptyString, isNullOrUndefined, isObject, snakeToCamelCase } from "@pineapplelab/util";
import { AppErrorStateMatcher } from "./errorStateMatcher";

export abstract class Base implements Base.IBase {

  __errorsByField: { [field: string]: string[] } = {};
  __errorByField: { [field: string]: string } = {};
  __errorByFieldByDecorator: { [field: string]: { [decorator: string]: string } } = {};
  __matcherByField: { [field: string]: AppErrorStateMatcher } = {};
  __errors: string[] = [];
  __hasError: boolean = false;

  /**
   *
   * All properties that start with '__' wont be mapped to the entity on save they will be removed!
   *
   * Default format is snake_case.
   *
  */
  toJson( camelCase = false ) {
    if (camelCase) {
      return this.baseToJson();
    }
    return this.toSnakeCaseJson();
  }

  fromJson(json: { [key: string]: any }) {
    this.fromJsonToBase(json);
    return this;
  }

  private baseToJson() {
    const trueValues: any[] = [];
    const text =  JSON.stringify( this, ( (key, value) => {
      if (this.isSensitiveValue( value )) {
        return `$__sensitiveValue${trueValues.push( value ) - 1}`;
      }
      return value;
    } ) );
    const json = JSON.parse( text, ( key, value ) => {
      if (isNotEmptyString( value ) && value.startsWith('$__sensitiveValue')) {
        return trueValues[ parseInt( value.slice(17), 10 ) ];
      }
      return value;
    } );
    this.removeAllPrivetProperties( json );
    return json;
  }

  private toSnakeCaseJson() {
    const json = this.baseToJson();
    this.toSnakeCaseAllSubObjects( json );
    // Some snake_case fields (e.g. `_skip_balance_trigger`) have a single leading
    // underscore that gets absorbed into capitalizing the first letter during the
    // snake->camel conversion in fromJsonToBase() (e.g. `_skip_balance_trigger` ->
    // `SkipBalanceTrigger`), so removeAllPrivetProperties() in baseToJson() doesn't
    // recognize them as private at that point (no leading underscore yet). The
    // camel->snake pass above then faithfully reconstructs the original underscore-
    // prefixed name, leaking transient/internal fields (loaded from Firestore) back
    // into the outgoing payload. Strip again now that keys are back in snake_case.
    this.removeAllPrivetProperties( json );
    return json;
  }

  private fromJsonToBase( json: { [key: string]: any } ) {
    this.toCamelCaseAllSubObjects( json );
    Object.assign( this, json );
  }

  private toCamelCaseAllSubObjects(object: any) {
    this.modifyEveryKeyInAObject(object, snakeToCamelCase );
  }

  private toSnakeCaseAllSubObjects( object: any ) {
    this.modifyEveryKeyInAObject(object, camelToSnakeCase );
  }

  private modifyEveryKeyInAObject(object: any, keyModifier: (key: string) => string ) {
    this.doItForEveryKeyInAObject( object, (key, _object) => {
      const snakeKey = keyModifier( key );
      if (snakeKey === key) {
        return ;
      }
      _object[ snakeKey ] = _object[key];
      delete _object[key];
    });
  }

  private removeAllPrivetProperties(object: any) {
    this.doItForEveryKeyInAObject(object, (key, _object) => {
      if (key.startsWith('__') || key.startsWith('_')) {
        delete _object[key];
      }
    });
  }

  private doItForEveryKeyInAObject(object: any, it: (key: string, object: any) => void, deepLevel = 0 ) {
    deepLevel++;
    if (deepLevel > DEEP_LEVEL_MAX || !isObject(object) || this.isSensitiveValue( object ) ) {
      return ;
    }
    if (isArray(object as any[])) {
      return object.map( (item: any) => this.doItForEveryKeyInAObject(item, it, deepLevel ) );
    }
    Object.keys( object ).map( key => {
      if (isObject( object[key] )) {
        this.doItForEveryKeyInAObject( object[key], it, deepLevel );
      }
      it(key, object);
    } );
  }

  private isSensitiveValue( object: any ) {
    if (isNullOrUndefined( object )) {
      return false;
    }
    const types = [firebase.firestore.DocumentReference, firebase.firestore.FieldValue, AppErrorStateMatcher];
    return types.some( type => object instanceof type );
  }

  isValid() {
    const result = Validator.validate( this );
    this.__hasError = !result.valid;
    this.__errorByField = result.fieldError;
    this.__errorsByField = result.fieldErrors;
    this.__errors = result.errors;
    this.__errorByFieldByDecorator = result.fieldDecoratorError;
    Object.keys( this.__errorByField ).map( field => {
      if (isNullOrUndefined( this.__matcherByField[field] )) {
        this.__matcherByField[field] = new AppErrorStateMatcher( this, field );
      }
    });
    return !this.__hasError;
  }

}

export namespace Base {

  export interface IModelRelationship {
    id: string;
    reference: firebase.firestore.DocumentReference;
    name: string;
  };

  export interface IBase {
    __matcherByField: { [field: string]: AppErrorStateMatcher };
    __errorByField: { [field: string]: string };
    isValid: () => boolean;
    [key: string]: any;
  };

}

const DEEP_LEVEL_MAX = 3;
